import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import { AdditionalChannelTabs, Channel } from '@/models';
import {
  useAllChannelTabData,
  useEmailData,
  useEventData,
  useFaceToFaceData,
  useWebsiteData,
} from '@/pages/ChannelFocus/useChannelCoverageHooks';
import NavigationTabList, {
  type NavigationTab,
  type NavigationTabListProps,
} from '@/storybook/components/NavigationTabList/NavigationTabList';
import Score from '@/storybook/components/Score/Score';
import { AppRoutes } from '@/utils';
import { ReactNode, useCallback, useMemo } from 'react';

interface ChannelFocusNavigationProps {
  activeChannel: Channel | AdditionalChannelTabs.ALL_CHANNEL | undefined;
  children: ReactNode;
}

const NAVIGATION_TAB_ADOBE_ANALYTICS_CONFIG: NavigationTabListProps['adobeTrackingAnalytics'] = {
  trackingPosition: AdobeAnalyticsConst.POSITIONS.CHANNEL_FOCUS_TABS,
  trackingPrefixName: AdobeAnalyticsConst.LINKCLICK_NAMES.DASHBOARD_NAVIGATION_TABS,
};

interface ChannelTabScoreProps {
  channelId: AdditionalChannelTabs | Channel;
  currentValue: number | undefined;
  prevValue: number | undefined;
  isActive: boolean;
}

const ChannelTabScore = ({ channelId, currentValue, prevValue, isActive }: ChannelTabScoreProps) => {
  const hasCoverage = currentValue !== undefined;
  const arrowValue = hasCoverage && prevValue ? (currentValue / prevValue - 1) * 100 : undefined;

  return (
    <Score
      value={hasCoverage ? currentValue * 100 : 0}
      isRoundedValue={true}
      displayZero={hasCoverage}
      valueSuffix="%"
      arrowValue={arrowValue}
      size="SM"
      label="Coverage"
      labelColor={isActive ? 'BLUE' : 'GREY'}
      align="center"
      data-testid={channelId + '-tab-score'}
      value-testid={channelId + '-tab-score-value'}
      dash-value-testid={channelId + '-tab-score-dash-value'}
      arrow-testid={channelId + '-tab-score-arrow-value'}
      reverseTitles={true}
    />
  );
};

const createChannelTab = (
  id: AdditionalChannelTabs | Channel,
  label: string,
  link: AppRoutes,
  isLoading: boolean,
  currentValue: number | undefined,
  prevValue: number | undefined,
  isActive: boolean,
): NavigationTab => ({
  'id': id,
  'data-testid': id,
  'label': label,
  'link': link,
  'isLoading': isLoading,
  'additionalContent': (
    <ChannelTabScore channelId={id} currentValue={currentValue} prevValue={prevValue} isActive={isActive} />
  ),
});

const useChannelNavigationTabs = (
  activeChannel: Channel | AdditionalChannelTabs.ALL_CHANNEL | undefined,
): NavigationTab[] => {
  const {
    isLoading: isLoadingAllChannel,
    current: currentAllChannel,
    previous: previousAllChannel,
  } = useAllChannelTabData();
  const {
    isLoading: isLoadingFaceToFace,
    current: currentFaceToFace,
    previous: previousFaceToFace,
  } = useFaceToFaceData();
  const { isLoading: isLoadingEmail, current: currentEmail, previous: previousEmail } = useEmailData();
  const { isLoading: isLoadingEvent, current: currentEvent, previous: previousEvent } = useEventData();
  const { isLoading: isLoadingWebsite, current: currentWebsite, previous: previousWebsite } = useWebsiteData();

  const getIsChannelActive = useCallback(
    (tabId: AdditionalChannelTabs | Channel) => activeChannel === tabId,
    [activeChannel],
  );

  return useMemo(
    () => [
      createChannelTab(
        AdditionalChannelTabs.ALL_CHANNEL,
        'ALL CHANNELS',
        AppRoutes.CHANNEL_ALL_CHANNELS,
        isLoadingAllChannel,
        currentAllChannel,
        previousAllChannel,
        getIsChannelActive(AdditionalChannelTabs.ALL_CHANNEL),
      ),
      createChannelTab(
        Channel.VISITS_1_TO_1,
        '1 TO 1 VISIT',
        AppRoutes.CHANNEL_FOCUS_1_TO_1,
        isLoadingFaceToFace,
        currentFaceToFace,
        previousFaceToFace,
        getIsChannelActive(Channel.VISITS_1_TO_1),
      ),
      createChannelTab(
        Channel.EMAIL,
        'EMAIL',
        AppRoutes.CHANNEL_FOCUS_EMAIL,
        isLoadingEmail,
        currentEmail,
        previousEmail,
        getIsChannelActive(Channel.EMAIL),
      ),
      createChannelTab(
        Channel.EVENT,
        'EVENT',
        AppRoutes.CHANNEL_FOCUS_EVENT,
        isLoadingEvent,
        currentEvent,
        previousEvent,
        getIsChannelActive(Channel.EVENT),
      ),
      createChannelTab(
        Channel.WEBSITE,
        'WEBSITE',
        AppRoutes.CHANNEL_FOCUS_WEBSITE,
        isLoadingWebsite,
        currentWebsite,
        previousWebsite,
        getIsChannelActive(Channel.WEBSITE),
      ),
    ],
    [
      currentAllChannel,
      currentEmail,
      currentEvent,
      currentFaceToFace,
      currentWebsite,
      getIsChannelActive,
      isLoadingAllChannel,
      isLoadingEmail,
      isLoadingEvent,
      isLoadingFaceToFace,
      isLoadingWebsite,
      previousAllChannel,
      previousEmail,
      previousEvent,
      previousFaceToFace,
      previousWebsite,
    ],
  );
};

const ChannelFocusNavigation = ({ activeChannel, children }: ChannelFocusNavigationProps) => {
  const navigationTabList = useChannelNavigationTabs(activeChannel);

  return (
    <>
      <NavigationTabList tabs={navigationTabList} adobeTrackingAnalytics={NAVIGATION_TAB_ADOBE_ANALYTICS_CONFIG} />
      {children}
    </>
  );
};

export default ChannelFocusNavigation;
